import { useMemo } from 'react';
import type { IApiErrorPlain, Lang } from '@platform/core/types';
import { groupByCategory } from '@platform/navigation';
import { useGetCatalogQuery, useGetModuleDefinitionsQuery } from '@platform/state/api';
import type { IPartyCalendarData } from './IPartyCalendarData';
import { partyEvents } from './partyCalendarModel';
import { useRecordsOfTypesQuery } from './partyCalendarApi';
import { PARTY_CATEGORY, partyModuleKeys, partyTypes } from './partyTypes';

/**
 * Дані календаря партії простору (прототип v4 pcal): модулі «Party & Group», встановлені в просторі (каталог простору, порядок — група меню
 * «Партія»), їхні визначення, злиті з налаштуваннями простору (ADR-023), реалізовані типи записів з відтінками, усі записи цих типів і події
 * з першої заповненої дати кожного запису.
 */
export function usePartyCalendar(ws: string, lang: Lang): IPartyCalendarData {
  const catalog = useGetCatalogQuery({ workspaceId: ws });
  const keys = useMemo(() => partyModuleKeys(catalog.data?.modules ?? [], groupByCategory(PARTY_CATEGORY)?.modules ?? []), [catalog.data]);
  const definitions = useGetModuleDefinitionsQuery({ keys: keys.join(','), workspaceId: ws }, { skip: !keys.length });
  const types = useMemo(() => partyTypes(definitions.data ?? [], keys, lang), [definitions.data, keys, lang]);
  const typeKeys = useMemo(() => types.map((t) => t.key), [types]);
  const records = useRecordsOfTypesQuery({ ws, types: typeKeys }, { skip: !typeKeys.length });
  const events = useMemo(() => partyEvents(types, records.data ?? {}), [types, records.data]);
  return {
    types, events,
    isLoading: catalog.isLoading || definitions.isLoading || records.isLoading,
    error: (catalog.error ?? definitions.error ?? records.error) as IApiErrorPlain | undefined,
  };
}
